import { CONTENT_FITNESS_VERIFICATION_STATE } from "contracts/enums/content-fitness";
import type { ContentFitnessSubcriterion } from "contracts/report/content-fitness/subcriteria";
import { createMemo, For, Show } from "solid-js";
import { pluralize } from "../../ui/format";
import { CLIENT_TERMS } from "./clientLanguage";
import {
  type AssessmentRow,
  rowsForSubcriterion,
} from "./contentFitnessDimensionModel";
import { EmptyState } from "./ScanContentFitnessParts";

export function CanonicalSubcriteriaSummary(props: {
  subcriteria: readonly ContentFitnessSubcriterion[];
  rows: AssessmentRow[];
  loading: boolean;
}) {
  return (
    <section class="scan-panel">
      <div class="scan-section-head">
        <div>
          <span class="scan-kicker">{CLIENT_TERMS.assessmentCriteria}</span>
          <h2>What this dimension assesses</h2>
          <p class="scan-muted">
            Each criterion shows what this scan assessed. Criteria not assessed
            remain visible and are not treated as zero.
          </p>
        </div>
      </div>
      <Show
        when={props.subcriteria.length > 0}
        fallback={
          <EmptyState
            loading={props.loading}
            text="No assessment criteria are configured for this dimension."
          />
        }
      >
        <div class="scan-subcriterion-summary-list">
          <For each={props.subcriteria}>
            {(subcriterion) => (
              <SubcriterionSummary
                subcriterion={subcriterion}
                rows={rowsForSubcriterion(props.rows, subcriterion.key)}
              />
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

function SubcriterionSummary(props: {
  subcriterion: ContentFitnessSubcriterion;
  rows: AssessmentRow[];
}) {
  const scoredRows = createMemo(() =>
    props.rows.filter(
      (row) =>
        row.assessment.verificationState ===
          CONTENT_FITNESS_VERIFICATION_STATE.SCORED &&
        row.assessment.score !== null,
    ),
  );
  const pageCount = createMemo(
    () => new Set(scoredRows().map((row) => row.page.id)).size,
  );
  return (
    <article class="scan-subcriterion-summary">
      <div class="scan-subcriterion-summary-head">
        <div>
          <strong>{props.subcriterion.label}</strong>
          <span>{summaryState(props.subcriterion, props.rows)}</span>
        </div>
        <b>{averageScore(scoredRows())}</b>
      </div>
      <p>{props.subcriterion.definition}</p>
      <small>
        {props.subcriterion.pilot
          ? `${pageCount()} ${pluralize(pageCount(), "page")} scored${unverifiedSuffix(props.rows)}`
          : "Not assessed in this scan"}
      </small>
    </article>
  );
}

function averageScore(rows: AssessmentRow[]): string {
  if (rows.length === 0) return "--";
  const average =
    rows.reduce((sum, row) => sum + (row.assessment.score ?? 0), 0) /
    rows.length;
  return `${Number(average.toFixed(1))}/4 avg`;
}

function summaryState(
  subcriterion: ContentFitnessSubcriterion,
  rows: AssessmentRow[],
): string {
  if (!subcriterion.pilot) return "Not assessed in this scan";
  if (
    rows.some(
      (row) =>
        row.assessment.verificationState ===
        CONTENT_FITNESS_VERIFICATION_STATE.SCORED,
    )
  ) {
    return rows.every(
      (row) =>
        row.assessment.verificationState ===
        CONTENT_FITNESS_VERIFICATION_STATE.SCORED,
    )
      ? "Scored"
      : "Partially scored";
  }
  if (
    rows.some(
      (row) =>
        row.assessment.verificationState ===
        CONTENT_FITNESS_VERIFICATION_STATE.UNABLE_TO_VERIFY,
    )
  ) {
    return "Unable to verify";
  }
  return rows.length > 0 ? "Not applicable" : "Not assessed";
}

function unverifiedSuffix(rows: AssessmentRow[]): string {
  const count = rows.filter(
    (row) =>
      row.assessment.verificationState ===
      CONTENT_FITNESS_VERIFICATION_STATE.UNABLE_TO_VERIFY,
  ).length;
  return count > 0 ? ` / ${count} unable to verify` : "";
}
